/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable react/require-default-props */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import React, {
  ChangeEvent,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { useField } from '@unform/core';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Button from '../../../Button';
import { Container } from './styles';

interface Props {
  name: string;
  title: string;
  maxWidth?: number;
  maxHeight?: number;
  maxSizeKB?: number;
  quality?: number; // compressão de 0.1 a 1.0
}

type InputProps = JSX.IntrinsicElements['input'] & Props;

// 🔹 Função de compressão/redimensionamento
function resizeImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
  quality: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      let { width, height } = img;

      // Mantém proporção
      if (width > maxWidth) {
        height = Math.floor((height * maxWidth) / width);
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.floor((width * maxHeight) / height);
        height = maxHeight;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject('Canvas não suportado');
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (!blob) {
            reject('Erro ao criar blob da imagem');
            return;
          }
          const newFile = new File([blob], file.name, { type: blob.type });
          resolve(newFile);
        },
        'image/jpeg', // pode trocar para webp se quiser
        quality,
      );
    };

    reader.onerror = err => reject(err);
    reader.readAsDataURL(file);
  });
}

const FileInputSpecial: React.FC<InputProps> = ({
  name,
  title,
  maxWidth = 1200,
  maxHeight = 1200,
  maxSizeKB = 500,
  quality = 0.8,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { fieldName, registerField, defaultValue, error } = useField(name);
  const [preview, setPreview] = useState(defaultValue);

  const handlePreview = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        setPreview(null);
        return;
      }

      let finalFile = file;

      // Só comprime/redimensiona se for imagem e maior que limite
      if (file.type.startsWith('image/') && file.size > maxSizeKB * 1024) {
        try {
          finalFile = await resizeImage(file, maxWidth, maxHeight, quality);
          console.log('Imagem comprimida:', finalFile);
        } catch (err) {
          console.error('Erro ao redimensionar:', err);
        }
      }

      // Atualiza preview
      if (finalFile.size > 5 * 1024 * 1024) {
        setPreview('Tamanho máximo de 5MB excedido');
      } else {
        setPreview(finalFile.name);
      }

      // 🔹 Substitui o arquivo no input
      if (inputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(finalFile);
        inputRef.current.files = dataTransfer.files;
      }
    },
    [maxWidth, maxHeight, maxSizeKB, quality],
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'files[0]', // Unform pega sempre o primeiro arquivo
      clearValue(ref: HTMLInputElement) {
        ref.value = '';
        setPreview(null);
      },
      setValue(_: HTMLInputElement, value: string) {
        setPreview(value);
      },
    });
  }, [fieldName, registerField]);

  return (
    <Container>
      <Button
        type="button"
        color="orange"
        padding="1"
        rounded
        style={{
          marginBottom: '2px',
          marginTop: '2px',
        }}
        onClick={() => inputRef.current?.click()}
      >
        {title ? `${title}` : 'Anexar Arquivo'}
      </Button>

      {preview &&
        (preview === 'Tamanho máximo de 5MB excedido' ? (
          <p style={{ color: 'red' }}>{preview}</p>
        ) : (
          <p>{preview}</p>
        ))}

      <input
        type="file"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handlePreview}
        {...rest}
      />

      {!!error && <span>{error}</span>}
    </Container>
  );
};

export default FileInputSpecial;
